/**
 * <shop-the-look-upsell> — ajoute les produits complémentaires sélectionnés au
 * panier, via <mini-cart> et fetchConfig(). « Tout ajouter » embarque aussi le
 * produit principal ; l'ajout à l'unité n'ajoute que l'article concerné.
 *
 * Les prix (data-price / data-compare-at-price, data-main-prices) sont ceux du
 * site, promos Sales Genius comprises, calculés en Liquid ; le composant ne fait
 * que les additionner.
 *
 * Markup : snippets/shop-the-look-upsell-block.liquid
 */
class ShopTheLookUpsell extends HTMLElement {
  constructor() {
    super()

    this.miniCart = document.querySelector('mini-cart')
    this.messageEl = this.querySelector('[data-message]')
    this.summaryEl = this.querySelector('[data-summary]')

    this.mainVariantId = Number(this.dataset.mainVariantId || 0)
    this.mainPrice = Number(this.dataset.mainPrice || 0)
    this.mainCompareAt = Number(this.dataset.mainCompareAtPrice || 0)
    // { [variantId]: [prix, prixBarré] } pour toutes les variantes du produit principal.
    this.mainPrices = this.parseMainPrices()
    this.mainVariantTitle = this.dataset.mainVariantTitle || ''
    this.mainSku = this.dataset.mainSku || ''
    this.currency = this.dataset.currency || ''

    // Garde-fou « Tout ajouter » : la PDP présélectionne une taille, on demande confirmation au
    // premier clic tant que le client n'a pas choisi lui-même (voir `syncMainVariant`).
    this.needsMainConfirm = this.hasAttribute('data-confirm-main-size')
    this.awaitingMainConfirm = false
    this.mainSizeOption = this.dataset.mainSizeOption || ''
    this.mainSize = this.dataset.mainSize || this.mainVariantTitle

    this.messages = {
      generic: this.dataset.errorGeneric,
      noVariant: this.dataset.errorNoVariant,
      selectVariant: this.dataset.errorSelectVariant,
      selectVariants: this.dataset.errorSelectVariants,
      skipped: this.dataset.infoSkipped,
      allUnavailable: this.dataset.infoAllUnavailable
    }

    this.addEventListener('change', this.onSelectChange.bind(this))
    this.addEventListener('click', this.onClick.bind(this))
  }

  connectedCallback() {
    // Variante principale : état courant du picker de la PDP, puis suivi de ses changements.
    this.syncMainVariant(this.getPickerVariant())
    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const sectionId = event?.data?.sectionId
      if (sectionId && this.dataset.pickerSection && sectionId !== this.dataset.pickerSection) return
      // Le client a choisi lui-même sa taille : plus rien à confirmer, on efface le rappel s'il est affiché.
      this.needsMainConfirm = false
      if (this.awaitingMainConfirm) {
        this.endMainConfirm(this.querySelector('[data-global-add]'))
        this.setMessage('')
      }
      this.syncMainVariant(event?.data?.variant)
    })
    this.computeTotals()
    this.observeView()
  }

  disconnectedCallback() {
    this.variantChangeUnsubscriber?.()
    this.viewObserver?.disconnect()
  }

  /**
   * Tracking : le composant émet des événements DOM (`shopTheLook:*`, bubbles) avec
   * les données produit ; `snippets/custom-tracking.liquid` les traduit en dataLayer.
   */
  track(name, detail = {}) {
    this.dispatchEvent(
      new CustomEvent(`shopTheLook:${name}`, {
        bubbles: true,
        detail: { currency: this.currency, parent: this.describeParent(), ...detail }
      })
    )
  }

  /** `view` une seule fois, quand la moitié du bloc est réellement visible. */
  observeView() {
    if (this.hasAttribute('data-empty') || this.viewObserver || typeof IntersectionObserver === 'undefined') return
    this.viewObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return
        this.viewObserver.disconnect()
        this.track('view', { items: this.describeItems([...this.querySelectorAll('[data-item]')]) })
      },
      { threshold: 0.5 }
    )
    this.viewObserver.observe(this)
  }

  describeParent() {
    return {
      productId: Number(this.dataset.mainProductId || 0),
      handle: this.dataset.mainHandle || '',
      title: this.dataset.mainTitle || '',
      vendor: this.dataset.mainVendor || '',
      type: this.dataset.mainType || '',
      variantId: this.mainVariantId,
      variantTitle: this.mainVariantTitle,
      sku: this.mainSku,
      price: this.mainPrice
    }
  }

  /** Ligne du bloc ; la variante n'est renseignée que si une taille est choisie. */
  describeItem(item) {
    const button = item.querySelector('[data-item-add]')
    return {
      productId: Number(item.dataset.itemId || 0),
      handle: item.dataset.itemHandle || '',
      title: item.dataset.itemName || '',
      vendor: item.dataset.itemVendor || '',
      type: item.dataset.itemType || '',
      variantId: Number(button?.dataset.variantId || 0),
      variantTitle: button?.dataset.variantTitle || '',
      sku: button?.dataset.sku || '',
      price: Number(button?.dataset.price || 0),
      available: !item.hasAttribute('data-unavailable') && !button?.hasAttribute('data-notify')
    }
  }

  describeItems(items) {
    return items.map((item) => this.describeItem(item))
  }

  /** `<variant-selects>` ou `<variant-radios>` selon le réglage du picker, tous deux exposent `currentVariant`. */
  getPickerVariant() {
    const sectionId = this.dataset.pickerSection
    const selector = sectionId
      ? `variant-selects[data-section="${sectionId}"], variant-radios[data-section="${sectionId}"]`
      : 'variant-selects, variant-radios'
    return document.querySelector(selector)?.currentVariant || null
  }

  parseMainPrices() {
    try {
      return JSON.parse(this.dataset.mainPrices || '{}')
    } catch (e) {
      return {}
    }
  }

  /** Prix Sales Genius de la variante principale ; repli sur le prix catalogue transmis par le picker. */
  syncMainVariant(variant) {
    if (!variant) return
    if (variant.id) this.mainVariantId = Number(variant.id)
    if (variant.title) this.mainVariantTitle = variant.title
    if (typeof variant.sku === 'string') this.mainSku = variant.sku
    this.mainSize = (this.mainSizeOption && variant[this.mainSizeOption]) || variant.title || this.mainSize
    const known = this.mainPrices[String(variant.id)]
    if (Array.isArray(known)) {
      this.mainPrice = Number(known[0] || 0)
      this.mainCompareAt = Number(known[1] || 0)
    } else if (typeof variant.price === 'number') {
      this.mainPrice = variant.price
      this.mainCompareAt = Number(variant.compare_at_price || 0)
    }
    this.computeTotals()
  }

  formatMoney(cents) {
    return theme.Currency.formatMoney(cents, theme.shopSettings.moneyFormat)
  }

  priceMarkup(price, compareAt = 0) {
    const current = `<price-money><bdi>${this.formatMoney(price)}</bdi></price-money>`
    if (compareAt > price)
      return `<s><price-money><bdi>${this.formatMoney(compareAt)}</bdi></price-money></s> ${current}`
    return current
  }

  setMessage(message, status = 'error') {
    if (!this.messageEl) return
    this.messageEl.classList.remove('shop-the-look-upsell__message--confirm')
    this.messageEl.textContent = message || ''
    this.messageEl.dataset.status = status
    this.messageEl.hidden = !message
  }

  /** La largeur est figée pendant le chargement : le libellé change mais le bouton ne bouge pas. */
  setLoading(button, isLoading) {
    if (!button) return
    if (isLoading) button.style.width = `${button.getBoundingClientRect().width}px`
    else button.style.removeProperty('width')
    button.classList.toggle('loading', isLoading)
    button.toggleAttribute('disabled', isLoading)
    button.setAttribute('aria-busy', String(isLoading))
    const label = isLoading ? button.dataset.addingLabel : button.dataset.addLabel
    if (label) button.textContent = label
  }

  /**
   * Premier « Tout ajouter » sans choix explicite de la taille principale : on rappelle la taille
   * qui partira au panier, avec un lien vers le picker, et le CTA devient « Confirmer et tout ajouter ».
   */
  askMainConfirm(button) {
    this.needsMainConfirm = false
    this.awaitingMainConfirm = true
    this.globalAddLabel = button.dataset.addLabel
    button.dataset.addLabel = this.dataset.confirmLabel || button.dataset.addLabel
    button.textContent = button.dataset.addLabel

    if (!this.messageEl) return
    const text = document.createElement('span')
    text.textContent = (this.dataset.confirmMessage || '%size%').replace('%size%', this.mainSize)
    const edit = document.createElement('button')
    edit.type = 'button'
    edit.className = 'link link--text'
    edit.setAttribute('data-edit-main-size', '')
    edit.textContent = this.dataset.editLabel || ''
    edit.setAttribute('aria-label', `${edit.textContent} : ${text.textContent}`)
    this.messageEl.replaceChildren(text, edit)
    this.messageEl.classList.add('shop-the-look-upsell__message--confirm')
    this.messageEl.dataset.status = 'info'
    this.messageEl.hidden = false
  }

  /** Fin de la confirmation (ajout effectué ou taille changée) : le CTA retrouve son libellé. */
  endMainConfirm(button) {
    if (!this.awaitingMainConfirm) return
    this.awaitingMainConfirm = false
    if (!button) return
    if (this.globalAddLabel) button.dataset.addLabel = this.globalAddLabel
    if (!button.classList.contains('loading')) button.textContent = button.dataset.addLabel
  }

  /** « Modifier » : amène au picker de la PDP et l'ouvre quand c'est possible. */
  focusMainPicker() {
    const sectionId = this.dataset.pickerSection
    const scope = sectionId ? `[data-section="${sectionId}"]` : ''
    const picker = document.querySelector(`variant-radios${scope}, variant-selects${scope}`)
    if (!picker) return

    const option = this.mainSizeOption ? picker.querySelector(`[data-option-index="${this.mainSizeOption}"]`) : null
    const target = option || picker
    target.scrollIntoView({ behavior: 'smooth', block: 'center' })

    // Select custom du thème (`select-wrapper[data-custom-style]`) : un <details> qu'on peut ouvrir.
    const disclosure = target.querySelector('details.disclosure--select')
    if (disclosure) {
      disclosure.setAttribute('open', '')
      disclosure.querySelector('summary')?.focus({ preventScroll: true })
      return
    }
    const control =
      target.querySelector('input[type="radio"]:checked') || target.querySelector('input[type="radio"], select')
    control?.focus({ preventScroll: true })
  }

  /** `BIS` est le global de l'app back-in-stock, absent si le widget ne charge pas. */
  notifyLabel(button) {
    if (typeof BIS !== 'undefined' && typeof BIS.currentButtonCaption === 'function') {
      const caption = BIS.currentButtonCaption()
      if (caption) return caption.trim()
    }
    return button.dataset.notifyLabel || button.dataset.addLabel || ''
  }

  /** `data-notify` sort l'item du panier ; `data-variant-id` reste lu par l'app back-in-stock. */
  setNotifyMode(button, isNotify, variantId) {
    if (!button) return
    button.classList.toggle('BIS_trigger', isNotify)
    button.toggleAttribute('data-notify', isNotify)
    if (variantId) button.dataset.variantId = String(variantId)
    button.textContent = isNotify ? this.notifyLabel(button) : button.dataset.addLabel || button.textContent
  }

  markNeedsChoice(items) {
    this.querySelectorAll('[data-item]').forEach((item) => {
      item.classList.toggle('shop-the-look-upsell__item--needs-choice', items.includes(item))
    })
  }

  focusChoice(item) {
    const control = item?.querySelector('[data-variant-select], [data-pills] .shop-the-look-upsell__pill')
    control?.focus()
  }

  getCurrentVariantId(item) {
    const button = item.querySelector('[data-item-add]')
    if (!button || button.hasAttribute('data-notify')) return 0
    return Number(button.dataset.variantId || 0)
  }

  getCurrentPrice(item) {
    const button = item.querySelector('[data-item-add]')
    if (!button || button.hasAttribute('data-notify')) return 0
    return Number(button.dataset.price || 0)
  }

  /** Prix avant promo de la ligne, ou son prix courant s'il n'y a rien à barrer. */
  getCurrentCompareAt(item) {
    const button = item.querySelector('[data-item-add]')
    if (!button || button.hasAttribute('data-notify')) return 0
    const price = Number(button.dataset.price || 0)
    const compareAt = Number(button.dataset.compareAtPrice || 0)
    return compareAt > price ? compareAt : price
  }

  partitionItems() {
    const toAdd = []
    const needChoice = []
    const skipped = []

    this.querySelectorAll('[data-item]').forEach((item) => {
      const button = item.querySelector('[data-item-add]')
      if (item.hasAttribute('data-unavailable') || button?.hasAttribute('data-notify')) {
        skipped.push(item)
        return
      }
      const variantId = this.getCurrentVariantId(item)
      if (variantId) toAdd.push(variantId)
      else needChoice.push(item)
    })

    return { toAdd, needChoice, skipped }
  }

  namesOf(items) {
    return items
      .map((item) => item.dataset.itemName)
      .filter(Boolean)
      .join(', ')
  }

  /** Total = somme des prix réels ; barré si au moins un article est en promo. */
  computeTotals() {
    let total = this.mainPrice
    let compareTotal = this.mainCompareAt > this.mainPrice ? this.mainCompareAt : this.mainPrice
    this.querySelectorAll('[data-item]').forEach((item) => {
      total += this.getCurrentPrice(item)
      compareTotal += this.getCurrentCompareAt(item)
    })

    const totalEl = this.summaryEl?.querySelector('[data-total]')
    if (totalEl) totalEl.innerHTML = this.priceMarkup(total, compareTotal)
  }

  /** Applique une variante choisie (dropdown ou pill) à la ligne : prix, bouton, totaux. */
  applyChoice(item, { variantId, price, compareAt, available, variantTitle, sku }) {
    if (!item) return
    const priceEl = item.querySelector('[data-item-price]')
    const itemBtn = item.querySelector('[data-item-add]')

    item.classList.remove('shop-the-look-upsell__item--needs-choice')

    if (priceEl) priceEl.innerHTML = this.priceMarkup(price, compareAt)
    if (itemBtn) {
      itemBtn.dataset.price = String(price)
      itemBtn.dataset.compareAtPrice = String(compareAt || 0)
      if (variantTitle !== undefined) itemBtn.dataset.variantTitle = variantTitle
      if (sku !== undefined) itemBtn.dataset.sku = sku
      this.setNotifyMode(itemBtn, !available, variantId)
    }
    this.computeTotals()
  }

  onSelectChange(event) {
    const select = event.target.closest('[data-variant-select]')
    if (!select) return

    const opt = select.options[select.selectedIndex]
    if (!opt || !opt.value) return

    const item = select.closest('[data-item]')
    this.applyChoice(item, {
      variantId: opt.value,
      price: Number(opt.dataset.price || 0),
      compareAt: Number(opt.dataset.compareAtPrice || 0),
      available: opt.dataset.available === 'true',
      variantTitle: opt.dataset.variantTitle || '',
      sku: opt.dataset.sku || ''
    })
    this.track('selectSize', { item: this.describeItem(item) })
  }

  onClick(event) {
    const pill = event.target.closest('.shop-the-look-upsell__pill')
    if (pill) return this.onPillClick(pill)

    const itemAdd = event.target.closest('[data-item-add]')
    if (itemAdd) return this.onItemAdd(itemAdd)

    if (event.target.closest('[data-edit-main-size]')) return this.focusMainPicker()

    const globalAdd = event.target.closest('[data-global-add]')
    if (globalAdd) return this.onGlobalAdd(globalAdd)
  }

  onPillClick(pill) {
    const container = pill.closest('[data-pills]')
    container?.querySelectorAll('.shop-the-look-upsell__pill').forEach((p) => p.setAttribute('aria-pressed', 'false'))
    pill.setAttribute('aria-pressed', 'true')

    const item = pill.closest('[data-item]')
    this.applyChoice(item, {
      variantId: pill.dataset.variantId,
      price: Number(pill.dataset.price || 0),
      compareAt: Number(pill.dataset.compareAtPrice || 0),
      available: pill.dataset.available === 'true',
      variantTitle: pill.dataset.variantTitle || '',
      sku: pill.dataset.sku || ''
    })
    this.track('selectSize', { item: this.describeItem(item) })
  }

  async onItemAdd(button) {
    // En mode alerte, le clic est traité par le handler global de l'app back-in-stock.
    if (button.hasAttribute('data-notify')) return

    const item = button.closest('[data-item]')
    if (!item) return

    const childVariantId = this.getCurrentVariantId(item)
    if (!childVariantId) {
      this.setMessage(this.messages.selectVariant, 'error')
      this.markNeedsChoice([item])
      this.focusChoice(item)
      return
    }

    this.markNeedsChoice([])
    const added = await this.addToCart([childVariantId], button, { withParent: false })
    if (added) {
      this.track('addSingle', { items: [this.describeItem(item)], parentAdded: this.lastAddIncludedParent })
    }
  }

  async onGlobalAdd(button) {
    const { toAdd, needChoice, skipped } = this.partitionItems()

    if (needChoice.length) {
      this.setMessage(`${this.messages.selectVariants} ${this.namesOf(needChoice)}`, 'error')
      this.markNeedsChoice(needChoice)
      this.focusChoice(needChoice[0])
      return
    }

    this.markNeedsChoice([])

    if (this.needsMainConfirm) return this.askMainConfirm(button)

    const added = await this.addToCart(toAdd, button)
    const addedItems = [...this.querySelectorAll('[data-item]')].filter((item) =>
      toAdd.includes(this.getCurrentVariantId(item))
    )
    if (added && (addedItems.length || this.lastAddIncludedParent)) {
      this.track('addAll', {
        items: this.describeItems(addedItems),
        skipped: this.describeItems(skipped),
        parentAdded: this.lastAddIncludedParent
      })
    }
    if (!added || !skipped.length) return

    if (toAdd.length) {
      this.setMessage(`${this.messages.skipped} ${this.namesOf(skipped)}`, 'info')
    } else {
      this.setMessage(this.messages.allUnavailable, 'info')
    }
  }

  /**
   * Avec `withParent` (« Tout ajouter »), le produit principal part avec les compléments,
   * sauf s'il est déjà au panier (d'où la lecture du panier avant l'ajout). L'ajout à
   * l'unité (`withParent: false`) n'envoie que l'article de la ligne.
   * @returns {Promise<boolean>} succès de l'ajout
   */
  async addToCart(childVariantIds, trigger, { withParent = true } = {}) {
    const parentVariantId = withParent ? this.mainVariantId : 0
    if (withParent && !parentVariantId) {
      this.setMessage(this.messages.noVariant, 'error')
      return false
    }

    this.lastAddIncludedParent = false
    if (document.body.classList.contains('template-cart') || !theme.shopSettings.cartDrawer) {
      const items = childVariantIds.map((id) => ({ id: Number(id), quantity: 1 }))
      if (parentVariantId) items.unshift({ id: parentVariantId, quantity: 1 })
      Shopify.postLink2(theme.routes.cart_add_url, { parameters: { items } })
      return false
    }

    // Tout ajout (ligne ou global) clôt une éventuelle demande de confirmation en cours.
    this.endMainConfirm(this.querySelector('[data-global-add]'))
    this.setMessage('')
    this.setLoading(trigger, true)

    try {
      const cart = await fetch(`${theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' }
      }).then((r) => r.json())
      const parentInCart = (cart.items || []).some((i) => Number(i.variant_id) === parentVariantId)
      this.lastAddIncludedParent = Boolean(parentVariantId) && !parentInCart

      const items = []
      if (this.lastAddIncludedParent) items.push({ id: parentVariantId, quantity: 1 })
      childVariantIds.forEach((id) => items.push({ id: Number(id), quantity: 1 }))

      // Rien à envoyer (principal déjà au panier, aucun complémentaire) : pas un échec.
      if (!items.length) return true

      const sections = this.miniCart ? this.miniCart.getSectionsToRender().map((s) => s.id) : []
      const body = JSON.stringify({ items, sections, sections_url: window.location.pathname })

      const response = await fetch(theme.routes.cart_add_url, { ...fetchConfig('javascript'), body }).then((r) =>
        r.json()
      )

      if (response.status) {
        this.setMessage(response.description || response.message || this.messages.generic, 'error')
        return false
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'shop-the-look-upsell',
        productVariantId: parentVariantId || Number(childVariantIds[0])
      })
      if (this.miniCart) this.miniCart.renderContents(response)
      return true
    } catch (e) {
      console.error('[shop-the-look-upsell]', e)
      this.setMessage(this.messages.generic, 'error')
      return false
    } finally {
      this.setLoading(trigger, false)
      this.computeTotals()
    }
  }
}

customElements.define('shop-the-look-upsell', ShopTheLookUpsell)
